import type {ToolPrettyError} from '../types/base-tool-types';

/**
 * Nesting constraint error patterns used by the render manifest.
 * Each entry provides:
 *  - match: substring matched against the raw error (used by toolMessageUtils for the user-facing message
 *           and by getRenderRecoveryInstructions for the AI-facing instruction)
 *  - message: human-friendly text shown in the chat UI
 *  - aiInstruction: directive injected into the tool response so the LLM knows how to recover
 */
export const RENDER_NESTING_ERROR_PATTERNS: ToolPrettyError[] = [
  {
    match: 'The Page Slot cannot be removed',
    message:
      'The Page Slot is required page infrastructure and cannot be removed.',
    aiInstruction:
      'HARD STOP — Do NOT retry render with insertMode "remove" or "replace" targeting a Page Slot. Preserve the Page Slot and remove only its child component instances when the user explicitly asks to clear content. To build a generated section from scratch, use create_reusable_section with placement targeting this Page Slot. To insert an existing component, render one <wf-component component-id="..."> with append/after on the Page Slot or one of its child instances.',
  },
  {
    match: 'can not be placed in a List',
    message:
      'The target element only accepts list items as children. Target a different parent or wrap your content in a list item.',
    aiInstruction:
      'Use insertMode "after" with the List element\'s own ID as insertTargetElementId. Do NOT retry into the List.',
  },
  {
    match: 'Cannot insert elements directly into a component instance',
    message:
      'Component instances cannot accept children. Use prop-* attributes to modify this component, or target its parent with before/after.',
    aiInstruction:
      'Use prop-* attributes to modify this component, or target its parent with before/after. Do NOT retry insertion.',
  },
  {
    match: 'Elements cannot be added to Tab Link',
    message:
      'Tab Link elements have a fixed structure. To add content, target the Tab Pane element instead.',
    aiInstruction:
      'Target the corresponding Tab Pane element instead. Do NOT retry.',
  },
  {
    match: 'Elements cannot be added to Slider elements',
    message:
      'The target element is a Slider — its internal structure is managed by Webflow. Target a Slider Slide (inside the Slider Mask) to add content instead.',
    aiInstruction:
      'Target a Slider Slide inside the Slider Mask, or use before/after on the Slider itself. Do NOT retry with the Slider as target.',
  },
  {
    match: 'Elements cannot be added to Form Block elements',
    message:
      'The target element is a Form Block — its structure is fixed. Target an element inside the form or add content before/after it instead.',
    aiInstruction:
      'Target an element inside the form or use before/after on the Form Block. Do NOT retry.',
  },
  {
    match: 'Elements cannot be added to Tabs Menu elements',
    message:
      'The target element is a Tabs Menu — it only accepts Tab Link children. To add tab content, target the Tab Pane element inside Tabs Content instead.',
    aiInstruction:
      'Target the Tab Pane inside Tabs Content instead. Do NOT retry with Tabs Menu.',
  },
  {
    match: 'Only Components are allowed in a Page Slot',
    message:
      'Page Slots only accept component instances. Insert a <wf-component> (PRESERVE with data-w-id, or INSERT with component-id) — never plain HTML.',
    aiInstruction:
      'Page Slots only accept <wf-component> instances. Do NOT retry with plain HTML (div/p/a/text), remove, or replace targeting the Page Slot. For a generated section from scratch, use create_reusable_section with placement targeting the Page Slot. Use INSERT (component-id="...") for existing components, or PRESERVE (<wf-component data-w-id="...">) only while that instance is still on the page. For reorder: prefer move_element on page-automation-public-mcp-compound-elements; if using render, rewrite the Page Slot children in one call with PRESERVE instances in the new order — never remove first, and never rebuild as HTML.',
  },
];
